import { useState, useRef, useEffect } from "react";
import { ChevronDownIcon, PlusIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import { useApiQuery } from "@/api/hooks/useApiQuery";
import { useSelectedFile } from "../../hooks/useSelectedFile";
import { getDrafts } from "@/entities/DataAssociation/DataAssociation.queries";
import { useRouter } from "@/utils/router";
import { useApiMutation } from "@/api/hooks/useApiMutation";
import { createDraft as createDraftMutation } from "@/entities/DataAssociation/DataAssociation.mutations";
import { Spinner } from "@/ui/atoms/Spinner";
import { useSession } from "@/entities/Session";
import { DraftStatus } from "@/entities/DataAssociation/Draft.model";
import { Badge } from "@/ui";
import { DocumentType } from "@/entities/DataAssociation/File.model";

export function DraftMenu({ className }: { className?: string }) {
    const session = useSession();
    const router = useRouter();
    const { selectedFileId, selectedFile } = useSelectedFile();
    const draftList = useApiQuery(getDrafts);
    const drafts = draftList.data?.filter(
        (draft) =>
            draft.fileId === selectedFileId &&
            draft.createdBy === session.user?.id,
    );
    const selectedOption = router.params.draftId
        ? drafts?.find((draft) => router.params.draftId === draft.id)
        : null;
    const [isOpen, setIsOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLDivElement>(null);
    const isReadonly = !router.params.draftId;
    const canCreate = !drafts?.find(
        (draft) => draft.createdBy === session.user?.id,
    );
    const { mutate: createDraft, isPending: createPending } = useApiMutation(
        createDraftMutation,
        {
            onSuccess: (response) => {
                console.log("create response", { response });
                router.navigate(
                    `/beta/companies/${router.params.companyId}/labeling/files/${response.fileId}/drafts/${response.id}`,
                );
                setIsOpen(false);
            },
        },
    );

    const ogLabel =
        selectedFile?.documentType === DocumentType.LABELED
            ? "Original labeled file"
            : "Original file";

    const toggleMenu = () => {
        setIsOpen(!isOpen);
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                menuRef.current &&
                !menuRef.current.contains(event.target as Node) &&
                buttonRef.current &&
                !buttonRef.current.contains(event.target as Node)
            ) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isOpen]);

    if (draftList.isLoading)
        return (
            <div
                className={clsx(
                    "bg-selected h-5 w-[300px]",
                    "animate-pulse rounded",
                )}
            />
        );

    return (
        <div className="flex shrink-0 flex-col gap-2">
            {(selectedOption || isReadonly) && (
                <div className="relative">
                    <div
                        ref={buttonRef}
                        onClick={toggleMenu}
                        className={clsx(
                            "flex w-fit cursor-pointer items-center gap-1",
                            "heading-s subdued  transition-colors",
                            className,
                        )}
                    >
                        <span>
                            {selectedOption
                                ? `Draft ${selectedOption.id}`
                                : ogLabel}
                        </span>
                        {selectedOption?.status === DraftStatus.IN_PROGRESS && (
                            <Badge className="body-micro cursor-pointer bg-slate-950 text-white">
                                In Progress
                            </Badge>
                        )}
                        {selectedOption?.status === DraftStatus.COMMITTED && (
                            <Badge className="body-micro cursor-pointer bg-slate-950 text-white">
                                Committed
                            </Badge>
                        )}
                        <ChevronDownIcon
                            className={clsx(
                                "size-3 transition-transform duration-200",
                                isOpen && "rotate-180",
                            )}
                        />
                    </div>

                    {isOpen && (
                        <div
                            ref={menuRef}
                            className={clsx(
                                "bg-slate-800",
                                "rounded-lg shadow-400",
                                "custom-scrollbar max-h-[400px] overflow-auto p-2",
                                "absolute left-0 top-full z-50 mt-1",
                                "min-w-[160px]",
                                "flex flex-col gap-1",
                            )}
                        >
                            <button
                                type="button"
                                onClick={() => {
                                    router.navigate(
                                        `/beta/companies/${router.params.companyId}/labeling/files/${selectedFileId}`,
                                    );
                                    setIsOpen(false);
                                }}
                                className={clsx(
                                    "flex h-[36px] w-full items-center gap-2 rounded-md px-2 py-1.5",
                                    "menu-item hover:bg-slate-600",
                                    isReadonly &&
                                        "bg-gray-100 font-medium dark:bg-slate-700",
                                )}
                                role="menuitem"
                            >
                                <span className="heading-s">{ogLabel}</span>
                            </button>
                            {drafts?.map((draft) => (
                                <button
                                    key={draft.id}
                                    type="button"
                                    onClick={() => {
                                        router.navigate(
                                            `/beta/companies/${router.params.companyId}/labeling/files/${selectedFileId}/drafts/${draft.id}`,
                                        );
                                        setIsOpen(false);
                                    }}
                                    className={clsx(
                                        "flex h-[36px] w-full items-center gap-2 rounded-md px-2 py-1.5",
                                        "menu-item cursor-pointer hover:bg-slate-600",
                                        router.params.draftId === draft.id &&
                                            "bg-gray-100 font-medium dark:bg-slate-700",
                                    )}
                                    role="menuitem"
                                >
                                    <span className="heading-s cursor-pointer">
                                        Draft {draft.id}
                                    </span>
                                    <Badge className="body-micro cursor-pointer bg-slate-950 text-white">
                                        {draft.status ===
                                        DraftStatus.IN_PROGRESS
                                            ? "In Progress"
                                            : "Committed"}
                                    </Badge>
                                </button>
                            ))}
                            {canCreate && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        createDraft();
                                    }}
                                    disabled={createPending}
                                    className={clsx(
                                        "flex h-[36px] w-full items-center gap-2 rounded-md px-2 py-1.5",
                                        "menu-item hover:bg-slate-600",
                                    )}
                                    role="menuitem"
                                >
                                    <span className="heading-s flex items-center gap-2">
                                        {createPending
                                            ? "Creating draft..."
                                            : "Create draft"}
                                        {!createPending && (
                                            <PlusIcon className="size-4" />
                                        )}
                                        {createPending && (
                                            <Spinner className="size-4" />
                                        )}
                                    </span>
                                </button>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
